/**
 * Create-component dialog. Mirrors the environment dialog's structure: a
 * role-tile grid where each role suggests sensible defaults for name + slug;
 * picking a role auto-fills the name/slug unless the user has already
 * customised them.
 *
 * Role is immutable after creation (same reasoning as environment kind —
 * changing a `database` into a `cache` would silently shift safety defaults
 * downstream). To "change role" the operator deletes and recreates.
 *
 * **Multi-step for repo Components.** When `role === 'repo'`, the dialog
 * becomes a 2-step wizard: step 1 captures the repo URL / ref / path
 * alongside the usual role/name/slug fields; step 2 collects the GitHub
 * PAT and optionally registers a webhook. Failed webhook registration
 * surfaces a paste-into-GitHub fallback panel so the operator can finish
 * the upstream setup by hand. Non-repo roles stay single-step (no PAT,
 * no webhook).
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  ArrowLeft,
  Check,
  Loader2,
  Globe,
  Database,
  FolderOpen,
  Zap,
  ListOrdered,
  Search,
  Cog,
  Boxes,
  GitBranch,
} from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog.js';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import { Label } from '@/components/ui/Label.js';
import {
  api,
  ApiError,
  type Component,
  type ComponentRole,
  type DiscoveryProposal as DiscoveryProposalType,
} from '@/api/client.js';
import { cn } from '@/lib/utils.js';
import {
  DiscoveryPreview,
  defaultSelection,
  selectionKey,
  selectionsFromKeys,
} from '@/components/applications/discovery-preview.js';
import {
  PatField,
  RegisterWebhookCheckbox,
  ManualWebhookFallbackPanel,
  attemptWebhookSetup,
  createSecretFromPat,
  type ManualFallback,
} from '@/components/applications/webhook-setup-fields.js';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applicationId: string;
  applicationName: string;
  /**
   * Orgs the application belongs to — needed for the secret we create
   * in step 2 (Secret rows are org-scoped, not application-scoped, so
   * a PAT created here can be reused by sibling components later).
   */
  applicationOrgId: string;
  applicationSlug: string;
  /** Slugs already taken in this app — pre-validates on the client. */
  takenSlugs: string[];
  onCreated: () => void | Promise<void>;
  /**
   * Pre-select a specific role on open. Used by the new-app wizard's
   * component-loop pattern to suggest "add a repo first, then web,
   * then …". When unset, falls back to the first role whose default
   * slug isn't taken in this app.
   */
  initialRole?: ComponentRole;
}

/**
 * Public props for the inline form variant. Same as the dialog's
 * props minus open/onOpenChange, plus an explicit onCancel callback
 * (the dialog wraps the close button into onOpenChange; the inline
 * variant lets the parent decide what "cancel" means — e.g. the
 * new-app wizard treats it as "skip this iteration of the loop").
 *
 * `onCreated` receives the freshly-created Component so the parent
 * can track what's been added (the new-app wizard uses this to drive
 * its "next suggested role" picker).
 */
export interface AddComponentFormProps {
  applicationId: string;
  applicationName: string;
  applicationOrgId: string;
  applicationSlug: string;
  takenSlugs: string[];
  initialRole?: ComponentRole;
  onCreated: (component: Component) => void | Promise<void>;
  onCancel: () => void;
}

export interface RoleVisual {
  value: ComponentRole;
  label: string;
  hint: string;
  icon: React.ComponentType<{ className?: string | undefined }>;
  defaultName: string;
  defaultSlug: string;
}

export const ROLE_VISUALS: Record<ComponentRole, RoleVisual> = {
  web: {
    value: 'web',
    label: 'Web',
    hint: 'HTTP-fronted process — nginx, Apache, php-fpm, Node server.',
    icon: Globe,
    defaultName: 'Web',
    defaultSlug: 'web',
  },
  database: {
    value: 'database',
    label: 'Database',
    hint: 'Persistent store. MySQL, MariaDB, Postgres, Mongo.',
    icon: Database,
    defaultName: 'Database',
    defaultSlug: 'db',
  },
  files: {
    value: 'files',
    label: 'Files',
    hint: 'User uploads or asset dir. Drupal sites/default/files, WP uploads.',
    icon: FolderOpen,
    defaultName: 'Files',
    defaultSlug: 'files',
  },
  cache: {
    value: 'cache',
    label: 'Cache',
    hint: 'Redis, Memcached, Varnish.',
    icon: Zap,
    defaultName: 'Cache',
    defaultSlug: 'cache',
  },
  queue: {
    value: 'queue',
    label: 'Queue',
    hint: 'Background job broker. BullMQ on Redis, RabbitMQ, SQS.',
    icon: ListOrdered,
    defaultName: 'Queue',
    defaultSlug: 'queue',
  },
  search: {
    value: 'search',
    label: 'Search',
    hint: 'Solr, Elasticsearch, Meilisearch.',
    icon: Search,
    defaultName: 'Search',
    defaultSlug: 'search',
  },
  worker: {
    value: 'worker',
    label: 'Worker',
    hint: 'Long-running background process.',
    icon: Cog,
    defaultName: 'Worker',
    defaultSlug: 'worker',
  },
  repo: {
    value: 'repo',
    label: 'Repository',
    hint: 'Source-code repo. Discovery reads .deployit/ from here.',
    icon: GitBranch,
    defaultName: 'Repository',
    defaultSlug: 'repo',
  },
  other: {
    value: 'other',
    label: 'Other',
    hint: 'Anything that doesn\'t fit the above. Free-form.',
    icon: Boxes,
    defaultName: 'Other',
    defaultSlug: 'other',
  },
};

/**
 * Default role ordering. `repo` first because operators look at "what
 * does this app live in?" before drilling into runtime components.
 */
const ROLE_ORDER: ComponentRole[] = [
  'repo',
  'web',
  'database',
  'files',
  'cache',
  'queue',
  'search',
  'worker',
  'other',
];

type Step = 'basics' | 'github';

/**
 * Display labels for each SCM provider — used in the step-2 dialog
 * title and any other spot in this file that needs the human-facing
 * name. Per-provider docs / button copy lives in
 * `webhook-setup-fields.tsx` (PROVIDER_COPY) so we avoid duplicating
 * the entire copy table here; this is just the short label.
 */
const PROVIDER_LABEL: Record<'github' | 'gitlab' | 'bitbucket', string> = {
  github: 'GitHub',
  gitlab: 'GitLab',
  bitbucket: 'Bitbucket',
};

/**
 * Dialog wrapper. Renders `AddComponentForm` inside `<Dialog>`. The
 * inline form variant is exported separately so the new-app wizard
 * can render it without nesting dialogs.
 *
 * **Dismiss guard.** This is a wizard — multi-step state would be
 * lost on accidental dismissal — so we:
 *   - Disable click-outside + Escape via
 *     `preventDismissOnInteractOutside` on DialogContent.
 *   - Intercept the X close (and any other path that fires
 *     `onOpenChange(false)`) with a "discard your changes?" confirm.
 *   - Successful create bypasses the confirm via a ref guard, since
 *     a successful submit shouldn't ask if we want to keep the
 *     state we just persisted.
 */
export function CreateComponentDialog({
  open,
  onOpenChange,
  applicationId,
  applicationName,
  applicationOrgId,
  applicationSlug,
  takenSlugs,
  onCreated,
  initialRole,
}: Props): JSX.Element {
  // Ref (not state) so the value is read inside the close handler
  // without forcing a re-render between "successful create" and
  // "Radix fires onOpenChange". State updates are async; refs aren't.
  const closingFromSuccess = useRef(false);

  // When a repo Component is created, we don't immediately close —
  // we switch the dialog to a final-review panel that runs discovery
  // and surfaces any `.deployit/` proposals the operator can apply
  // inline. Non-repo Components close immediately as before
  // (nothing to discover). Null when the form is still showing.
  const [reviewComponent, setReviewComponent] = useState<Component | null>(null);

  const handleOpenChange = (next: boolean): void => {
    if (!next && !closingFromSuccess.current) {
      const ok = window.confirm(
        'Discard your changes and close this wizard? Anything you typed will be lost.',
      );
      if (!ok) return;
    }
    closingFromSuccess.current = false;
    onOpenChange(next);
    if (!next) setReviewComponent(null); // reset for next open
  };

  /** Run after the operator finishes (or skips) the review. */
  const finishAndClose = async (): Promise<void> => {
    closingFromSuccess.current = true;
    await onCreated();
    onOpenChange(false);
    setReviewComponent(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* Wizard widened to max-w-xl (576px) — the default max-w-lg
          (512px) is too cramped for the 2-step repo flow's PAT field
          + branch picker row + register-webhook checkbox + multi-line
          description. The extra 64px gives header text breathing room
          and stops the PAT input + branch row from crowding the X.
          twMerge dedupes the base `max-w-lg` cleanly. */}
      <DialogContent preventDismissOnInteractOutside className="max-w-xl">
        {reviewComponent ? (
          <ComponentReviewPanel
            component={reviewComponent}
            applicationId={applicationId}
            applicationName={applicationName}
            onDone={() => void finishAndClose()}
          />
        ) : (
          <AddComponentForm
            applicationId={applicationId}
            applicationName={applicationName}
            applicationOrgId={applicationOrgId}
            applicationSlug={applicationSlug}
            takenSlugs={takenSlugs}
            {...(initialRole ? { initialRole } : {})}
            onCreated={async (component) => {
              if (component.role === 'repo') {
                // Repo Component: advance to review panel. Stays open
                // so the operator can confront discovery proposals
                // (or just see the summary) before navigating away.
                setReviewComponent(component);
                return;
              }
              // Non-repo Component: nothing to discover, close
              // immediately. Preserves the v0.0.61 behaviour for
              // database/web/files/etc. roles.
              await finishAndClose();
            }}
            onCancel={() => {
              // Cancel is an explicit "abandon" action — still go
              // through the confirm so a stray click on the Cancel
              // button doesn't immediately blow away mid-wizard state.
              handleOpenChange(false);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Final review panel rendered inside CreateComponentDialog after a
 * repo Component lands. Mirrors the new-app wizard's FinalReviewStep
 * but scoped to a single just-added Component:
 *
 *  - Summary card: "✓ Added <name> (<role>) to <appName>"
 *  - Synchronous /discover against the parent application — guarantees
 *    the proposal set is current even if the create handler's
 *    fire-and-forget auto-discovery is still in flight.
 *  - DiscoveryPreview with bulk-select toolbar + counted apply button
 *    when there are proposals; friendly empty state when not.
 *  - "Apply N and finish" applies + closes; "Skip for now" / "Go to
 *    application" close without applying.
 *
 * Identical UX to the new-app wizard's final-review — the difference
 * is the summary text (single component vs. whole app worth) and
 * that this surface always has a real repo Component to discover
 * against (vs. the wizard's FinalReviewStep which has to handle the
 * SaaS / no-repo case).
 */
function ComponentReviewPanel({
  component,
  applicationId,
  applicationName,
  onDone,
}: {
  component: Component;
  applicationId: string;
  applicationName: string;
  onDone: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const discoverQuery = useQuery({
    queryKey: ['discover', applicationId, 'component-final-review', component.id],
    queryFn: () => api.applications.discover(applicationId),
    retry: false,
    staleTime: 0,
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!discoverQuery.data) return;
    setSelected(defaultSelection(discoverQuery.data.proposals));
  }, [discoverQuery.data]);

  function toggle(key: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  function selectByPredicate(pred: (p: DiscoveryProposalType) => boolean): void {
    if (!discoverQuery.data) return;
    const next = new Set<string>();
    for (const p of discoverQuery.data.proposals) {
      if (p.kind === 'noop') continue;
      if (pred(p)) next.add(selectionKey(p));
    }
    setSelected(next);
  }

  const apply = useMutation({
    mutationFn: async () => {
      const selections = selectionsFromKeys(selected, discoverQuery.data?.proposals ?? []);
      if (selections.length === 0) return null;
      return api.applications.discoverApply(applicationId, selections);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['scripts'] });
      await qc.invalidateQueries({ queryKey: ['script'] });
      await qc.invalidateQueries({ queryKey: ['applications'] });
      await qc.invalidateQueries({ queryKey: ['pendingDiscovery'] });
      await qc.invalidateQueries({ queryKey: ['notifications'] });
      onDone();
    },
  });

  const hasProposals =
    discoverQuery.data &&
    (discoverQuery.data.totals.create > 0 ||
      discoverQuery.data.totals.update > 0 ||
      discoverQuery.data.totals.remove > 0);

  const selectedCount = discoverQuery.data
    ? selectionsFromKeys(selected, discoverQuery.data.proposals).length
    : 0;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Check className="h-5 w-5 text-emerald-500" />
          {component.name} added to {applicationName}
        </DialogTitle>
        <DialogDescription>
          DeployIt is scanning the repo for proposals from{' '}
          <span className="font-mono">.deployit/</span>. Apply what looks good
          now, or review later from the application page.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3 py-2">
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm">
          <p className="font-medium text-foreground">Created</p>
          <ul className="ml-4 mt-1 list-disc text-xs">
            <li>
              <span className="font-mono">{component.slug}</span>{' '}
              <span className="text-muted-foreground">({component.role})</span>
            </li>
          </ul>
        </div>

        {discoverQuery.isFetching && !discoverQuery.data && (
          <div className="flex items-center justify-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Scanning the repo for proposals…
          </div>
        )}

        {discoverQuery.error && (
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
            Couldn't run discovery now ({(discoverQuery.error as Error).message}). You can retry
            from the application page.
          </p>
        )}

        {discoverQuery.data && (
          <DiscoveryPreview
            report={discoverQuery.data}
            selected={selected}
            toggle={toggle}
            onSelectAll={() => selectByPredicate(() => true)}
            onSelectByKind={(k) => selectByPredicate((p) => p.kind === k)}
            onSelectNone={() => setSelected(new Set())}
            emptyStateMessage={
              <p className="rounded-md border border-border bg-muted/30 px-3 py-4 text-center text-sm text-muted-foreground">
                Nothing else to suggest — the application matches the repo's{' '}
                <span className="font-mono">.deployit/</span> already. You can push to
                trigger a rescan any time.
              </p>
            }
          />
        )}
      </div>

      <DialogFooter>
        {hasProposals ? (
          <>
            <Button variant="ghost" onClick={onDone} disabled={apply.isPending}>
              Skip for now
            </Button>
            <Button onClick={() => apply.mutate()} disabled={selectedCount === 0 || apply.isPending}>
              {apply.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {selectedCount > 0 ? `Apply ${selectedCount} and finish` : 'Apply selected'}
            </Button>
          </>
        ) : (
          <Button onClick={onDone}>Done</Button>
        )}
      </DialogFooter>
    </>
  );
}

/**
 * Inline form variant — the body of CreateComponentDialog without the
 * surrounding `<Dialog>` chrome. Used by the new-app wizard which
 * renders the form inside its own dialog instead of opening a
 * separate one.
 *
 * State resets only when the component (re)mounts — when the parent
 * chains multiple iterations, it should change the React `key` prop
 * between iterations so the form starts fresh for each new component.
 */
export function AddComponentForm({
  applicationId,
  applicationName,
  applicationOrgId,
  applicationSlug,
  takenSlugs,
  initialRole: initialRoleProp,
  onCreated,
  onCancel,
}: AddComponentFormProps): JSX.Element {
  const qc = useQueryClient();

  // initialRole prop wins when set (the new-app wizard pre-selects
  // 'repo' on first iteration, then 'web' / 'database' / … as the
  // operator walks the suggestion chain). Otherwise fall back to the
  // first role whose default slug isn't already taken in this app.
  const initialRole: ComponentRole =
    initialRoleProp ??
    ROLE_ORDER.find((r) => !takenSlugs.includes(ROLE_VISUALS[r].defaultSlug)) ??
    'other';
  const initialVisual = ROLE_VISUALS[initialRole];

  const [step, setStep] = useState<Step>('basics');
  const [role, setRole] = useState<ComponentRole>(initialRole);
  const [name, setName] = useState(initialVisual.defaultName);
  const [slug, setSlug] = useState(initialVisual.defaultSlug);
  const [description, setDescription] = useState('');

  // Repo-specific step-1 fields. URL is captured first; name + slug
  // auto-derive from it (operator can override). `pathInRepo` stays
  // in step 1 because it's a local concept the operator knows
  // without any API help. `defaultRef` is NOT here — it moves to
  // step 2's branch picker so we can populate from a SCM lookup
  // once the PAT lands. Defaults match the server-side
  // RepoComponentSettingsInputSchema defaults (pathInRepo='.').
  //
  // `scmKind` is auto-detected from the URL host (`github.com` →
  // 'github', `gitlab.com` → 'gitlab', `bitbucket.org` →
  // 'bitbucket'). It defaults to 'github' until a recognisable host
  // appears in the URL — keeps the existing single-provider behaviour
  // intact for everyone whose URLs match what they always did. The
  // detected kind drives both the lookup dispatch and the persisted
  // `settings.scmKind` on the new Component.
  const [repoUrl, setRepoUrl] = useState('');
  const [pathInRepo, setPathInRepo] = useState('.');
  const [scmKind, setScmKind] = useState<'github' | 'gitlab' | 'bitbucket'>('github');

  // Step-2 (github) state. PAT input + register-webhook checkbox +
  // branch-picker dropdown + manual-paste fallback. The branch
  // dropdown is empty until the operator clicks "Look up repository"
  // (or the lookup auto-runs when they enter a PAT — we use the
  // explicit button for v1 to keep the network behaviour predictable).
  const [pat, setPat] = useState('');
  // When non-null, the operator picked an existing PAT Secret from
  // the org's secrets list — submit uses this id as credentialsRef
  // directly, skipping the createSecretFromPat path. Mutually
  // exclusive with `pat` in practice (PatField clears the paste
  // field when an existing secret is selected).
  const [existingSecretId, setExistingSecretId] = useState<string | null>(null);
  const [defaultRef, setDefaultRef] = useState('');
  const [branchOptions, setBranchOptions] = useState<string[]>([]);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [registerWebhook, setRegisterWebhook] = useState(true);
  const [manualFallback, setManualFallback] = useState<ManualFallback | null>(null);

  // Track the Component the parent should hand-off to after the form
  // finishes. Set on every successful API create so the manual-paste
  // fallback (which leaves the form open) can still close-and-handoff
  // when the operator clicks Done.
  const [createdComponent, setCreatedComponent] = useState<Component | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Derive a sensible default name + slug + scmKind from a clone URL.
   * Skips if the operator has already customised either field (we
   * detect "customised" by checking against the role-default values,
   * which is approximate but good enough — same heuristic pickRole
   * uses).
   *
   * Supported hosts (kind detected from host):
   *   github.com    → 'github'
   *   gitlab.com    → 'gitlab'
   *   bitbucket.org → 'bitbucket'
   *
   * Supported URL shapes (per host):
   *   https://<host>/owner/repo
   *   https://<host>/owner/repo.git
   *   https://<host>/owner/repo/<extra-path>
   *
   * Returns the suggested name/slug + detected kind, or null when
   * parsing fails. A `null` return leaves the operator's existing
   * `scmKind` state in place rather than resetting it — useful for
   * the enterprise-host case where they may have manually picked the
   * kind already.
   */
  function suggestionsFromUrl(
    url: string,
  ): { name: string; slug: string; scmKind: 'github' | 'gitlab' | 'bitbucket' } | null {
    const trimmed = url.trim();
    const hosts: Array<{ pattern: RegExp; kind: 'github' | 'gitlab' | 'bitbucket' }> = [
      { pattern: /^https?:\/\/(?:www\.)?github\.com\/([^/\s]+)\/([^/\s]+?)(?:\.git)?(?:[/?#].*)?$/i, kind: 'github' },
      { pattern: /^https?:\/\/(?:www\.)?gitlab\.com\/([^/\s]+)\/([^/\s]+?)(?:\.git)?(?:[/?#].*)?$/i, kind: 'gitlab' },
      { pattern: /^https?:\/\/(?:www\.)?bitbucket\.org\/([^/\s]+)\/([^/\s]+?)(?:\.git)?(?:[/?#].*)?$/i, kind: 'bitbucket' },
    ];
    for (const { pattern, kind } of hosts) {
      const m = trimmed.match(pattern);
      if (m && m[2]) {
        const repo = m[2];
        return {
          name: repo.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          slug: repo.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
          scmKind: kind,
        };
      }
    }
    return null;
  }

  /**
   * On URL change, fill in name + slug + scmKind when they're still
   * at their role defaults (i.e. the operator hasn't customised
   * them). We don't overwrite a customised value because that would
   * feel surprising. `scmKind` is always updated when a known host
   * matches — it's an inferred field, not an operator-input one.
   */
  function handleRepoUrlChange(next: string) {
    setRepoUrl(next);
    if (role !== 'repo') return;
    const sug = suggestionsFromUrl(next);
    if (!sug) return;
    setName((cur) => (cur === '' || cur === ROLE_VISUALS.repo.defaultName ? sug.name : cur));
    setSlug((cur) => (cur === '' || cur === ROLE_VISUALS.repo.defaultSlug ? sug.slug : cur));
    setScmKind(sug.scmKind);
  }

  /**
   * Click "Look up repository" — calls /api/v1/scm/lookup with the
   * URL + PAT (if any). Populates the branch dropdown + pre-selects
   * the default branch. The lookup is cheap (one or two GitHub API
   * calls); we deliberately don't auto-run on PAT-change because the
   * operator may be mid-typing.
   */
  async function runLookup() {
    if (!repoUrl.trim()) {
      setLookupError('Enter a repository URL on the previous step first.');
      return;
    }
    setLookingUp(true);
    setLookupError(null);
    try {
      // Credential source: prefer an existing-secret pick (server
      // decrypts), else the pasted plaintext. The two are mutually
      // exclusive per the server's superRefine — never set both.
      const result = await api.scm.lookup({
        repoUrl: repoUrl.trim(),
        scmKind,
        ...(existingSecretId
          ? { secretId: existingSecretId }
          : pat.trim()
            ? { accessToken: pat.trim() }
            : {}),
      });
      setBranchOptions(result.branches);
      // Default branch pre-selected. If the operator had picked a
      // different ref earlier (re-lookup case) and it's still in the
      // returned list, keep their pick; else snap to the default.
      setDefaultRef((cur) =>
        cur && result.branches.includes(cur) ? cur : result.defaultBranch,
      );
      toast.success(
        `${result.fullName} — ${result.branches.length} branch${result.branches.length === 1 ? '' : 'es'} found`,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Lookup failed.';
      setLookupError(message);
    } finally {
      setLookingUp(false);
    }
  }

  function pickRole(r: ComponentRole) {
    const next = ROLE_VISUALS[r];
    setRole(r);
    setName((cur) => {
      const prev = Object.values(ROLE_VISUALS).find((v) => v.defaultName === cur && cur !== '');
      return prev ? next.defaultName : cur;
    });
    setSlug((cur) => {
      const prev = Object.values(ROLE_VISUALS).find((v) => v.defaultSlug === cur && cur !== '');
      return prev ? next.defaultSlug : cur;
    });
  }

  const slugTaken = slug.trim().length > 0 && takenSlugs.includes(slug.trim().toLowerCase());

  /**
   * Build the repo settings object that goes to POST /components — same
   * shape regardless of whether step 2 ran (just `credentialsRef`
   * conditionally included).
   */
  function buildRepoSettings(credentialsRef?: string): Record<string, unknown> {
    return {
      repoUrl: repoUrl.trim(),
      ...(defaultRef.trim() ? { defaultRef: defaultRef.trim() } : {}),
      pathInRepo: pathInRepo.trim() || '.',
      // scmKind is derived from the URL host in handleRepoUrlChange;
      // for an unrecognised host the state stays at its initial
      // 'github' default (the only kind we ship today). When other
      // providers land, an explicit picker will appear next to the
      // URL field for enterprise hosts the auto-detection misses.
      scmKind,
      ...(credentialsRef ? { credentialsRef } : {}),
    };
  }

  /**
   * Create the component + invalidate the cache so the new row shows up
   * in the Components card immediately. Used by all three "we're done"
   * paths (non-repo create, repo with PAT, repo without PAT).
   */
  async function createComponent(credentialsRef?: string): Promise<Component> {
    const settings = role === 'repo' ? buildRepoSettings(credentialsRef) : undefined;
    const created = await api.components.create(applicationId, {
      name,
      role,
      ...(slug ? { slug } : {}),
      ...(description ? { description } : {}),
      ...(settings ? { settings } : {}),
    });
    setCreatedComponent(created);
    await qc.invalidateQueries({ queryKey: ['components', applicationId] });
    return created;
  }

  /**
   * Step 1 submit. For non-repo roles, this is the entire flow — we
   * create the Component and close. For repo roles, advance to step 2
   * (PAT + webhook) without creating yet.
   */
  async function submitBasicsStep(e: FormEvent) {
    e.preventDefault();
    if (slugTaken) {
      setError(`A component with slug "${slug}" already exists in this application.`);
      return;
    }
    if (role === 'repo' && !repoUrl.trim()) {
      setError('Repository URL is required for a repo component.');
      return;
    }

    if (role !== 'repo') {
      // Single-step flow for non-repo roles. No PAT, no webhook.
      setSubmitting(true);
      setError(null);
      try {
        const created = await createComponent();
        toast.success(`Created ${name}`);
        await onCreated(created);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not create component.');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Repo role: advance to step 2. The Component itself is created in
    // step 2's submit (or skip) so the operator's PAT lands as part of
    // the same atomic flow rather than being a follow-up edit.
    setError(null);
    setStep('github');
  }

  /**
   * Step 2 submit (repo only). Pipeline mirrors the new-app wizard's
   * step-2 in CreateApplicationDialog:
   *   1. Create a Secret from the PAT (if provided).
   *   2. Create the Component with credentialsRef.
   *   3. If "register webhook" was ticked AND we have a credential,
   *      POST /webhook. Three outcomes — configured, manual-fallback,
   *      unexpected error.
   */
  async function submitGithubStep(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setManualFallback(null);
    try {
      // Branch: re-use an existing PAT secret if the operator picked
      // one from the dropdown, otherwise mint a new Secret from the
      // pasted plaintext. The id of either lands on the Component
      // as `credentialsRef`.
      const credentialsRef = existingSecretId
        ? existingSecretId
        : await createSecretFromPat({
            orgId: applicationOrgId,
            // Secret name reflects the provider so an org with both GitHub
            // and GitLab repos doesn't collide on `${app}-${slug}-pat`.
            name: `${applicationSlug}-${slug || 'repo'}-${scmKind}-pat`,
            pat,
            scmKind,
            applicationId,
            metadata: { source: 'create-component-wizard', scmKind },
          });

      const component = await createComponent(credentialsRef);

      if (registerWebhook && credentialsRef) {
        const outcome = await attemptWebhookSetup(component.id);
        if (outcome.kind === 'configured') {
          toast.success(`Created ${name} + webhook registered.`);
          await onCreated(component);
          return;
        }
        if (outcome.kind === 'manual-fallback') {
          // Render the panel and let the operator finish the upstream
          // setup by hand. Component exists; only the GitHub-side
          // webhook is missing. We don't call onCreated yet — the
          // parent shouldn't navigate away until the operator clicks
          // Done on the fallback panel.
          setManualFallback(outcome.fallback);
          toast.warning(
            `Created ${name}. Webhook needs manual setup — see panel below.`,
          );
          return;
        }
        // Unexpected (network / 5xx). Treat the Component as a success
        // and let the operator retry from the Components-card button.
        toast.warning(`Created ${name}. Webhook setup failed: ${outcome.message}`);
        await onCreated(component);
        return;
      }

      toast.success(
        credentialsRef
          ? `Created ${name}. Webhook can be set up later from the Components card.`
          : `Created ${name} (no credential — public-repo discovery only).`,
      );
      await onCreated(component);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create component.');
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Skip GitHub step entirely. Creates the Component without a
   * credential — operator can attach one later via the existing
   * Components-card flow.
   */
  async function skipGithubStep() {
    setSubmitting(true);
    setError(null);
    try {
      const created = await createComponent();
      toast.success(`Created ${name}.`);
      await onCreated(created);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create component.');
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Operator dismissed the manual-fallback panel after pasting into
   * GitHub. The Component already exists (created in submitGithubStep
   * before the webhook attempt); we just hand it back to the parent
   * here so the parent can navigate / chain.
   */
  async function finishAfterFallback() {
    setManualFallback(null);
    if (createdComponent) {
      await onCreated(createdComponent);
    }
  }

  return step === 'basics' ? (
    <BasicsStep
      applicationName={applicationName}
      role={role}
      pickRole={pickRole}
      name={name}
      setName={setName}
      slug={slug}
      setSlug={setSlug}
      slugTaken={slugTaken}
      description={description}
      setDescription={setDescription}
      repoUrl={repoUrl}
      onRepoUrlChange={handleRepoUrlChange}
      pathInRepo={pathInRepo}
      setPathInRepo={setPathInRepo}
      submitting={submitting}
      error={error}
      onCancel={onCancel}
      onSubmit={submitBasicsStep}
    />
  ) : (
    <GitHubStep
      componentName={name}
      scmKind={scmKind}
      applicationOrgId={applicationOrgId}
      pat={pat}
      setPat={setPat}
      existingSecretId={existingSecretId}
      onExistingSecretChange={setExistingSecretId}
      defaultRef={defaultRef}
      setDefaultRef={setDefaultRef}
      branchOptions={branchOptions}
      lookupError={lookupError}
      lookingUp={lookingUp}
      onLookup={runLookup}
      registerWebhook={registerWebhook}
      setRegisterWebhook={setRegisterWebhook}
      manualFallback={manualFallback}
      onFinishAfterFallback={finishAfterFallback}
      submitting={submitting}
      error={error}
      onBack={() => setStep('basics')}
      onSkip={skipGithubStep}
      onSubmit={submitGithubStep}
    />
  );
}

/* ---------------------------------------------------------------------------
 * Step 1: role + name/slug + (repo-only) URL/ref/path
 * ------------------------------------------------------------------------- */

interface BasicsStepProps {
  applicationName: string;
  role: ComponentRole;
  pickRole: (r: ComponentRole) => void;
  name: string;
  setName: (s: string) => void;
  slug: string;
  setSlug: (s: string) => void;
  slugTaken: boolean;
  description: string;
  setDescription: (s: string) => void;
  repoUrl: string;
  /**
   * Called instead of a plain setter on every keystroke — auto-derives
   * the suggested name + slug from `owner/repo` before propagating
   * the URL change. Operator-customised name/slug values are
   * preserved (handler checks for the role-default sentinel).
   */
  onRepoUrlChange: (s: string) => void;
  pathInRepo: string;
  setPathInRepo: (s: string) => void;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (e: FormEvent) => void;
}

function BasicsStep(props: BasicsStepProps): JSX.Element {
  const {
    applicationName,
    role,
    pickRole,
    name,
    setName,
    slug,
    setSlug,
    slugTaken,
    description,
    setDescription,
    repoUrl,
    onRepoUrlChange,
    pathInRepo,
    setPathInRepo,
    submitting,
    error,
    onCancel,
    onSubmit,
  } = props;

  return (
    <>
      <DialogHeader>
        {role === 'repo' && (
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Step 1 of 2
          </div>
        )}
        <DialogTitle>New component</DialogTitle>
        <DialogDescription>
          A component is something that runs inside{' '}
          <strong className="text-foreground">{applicationName}</strong>. Role is set now and can't
          be changed later (delete and recreate if you need to).
        </DialogDescription>
      </DialogHeader>

      <form className="space-y-4" onSubmit={onSubmit}>
        <div className="space-y-1.5">
          <Label>Role</Label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {ROLE_ORDER.map((r) => {
              const v = ROLE_VISUALS[r];
              const Icon = v.icon;
              return (
                <button
                  type="button"
                  key={r}
                  onClick={() => pickRole(r)}
                  className={cn(
                    'flex flex-col items-start gap-1.5 rounded-md border px-3 py-2.5 text-left transition-colors',
                    role === r
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-background hover:bg-accent/40',
                  )}
                >
                  <span className="flex items-center gap-2">
                    <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm font-medium">{v.label}</span>
                  </span>
                  <span className="text-[11px] leading-snug text-muted-foreground">{v.hint}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Repo URL FIRST when role=repo — owner/name derives the
            suggested component name + slug. Operator can still override
            either below. Branch picker lives on step 2 where we have
            the PAT to look the repo up against the upstream. The host
            also drives `scmKind` (github.com / gitlab.com /
            bitbucket.org) — there's no separate provider picker
            because the URL already tells us. */}
        {role === 'repo' && (
          <div className="space-y-1.5">
            <Label htmlFor="repoUrl">Repository URL</Label>
            <Input
              id="repoUrl"
              required
              autoFocus
              value={repoUrl}
              onChange={(e) => onRepoUrlChange(e.target.value)}
              placeholder="https://github.com/owner/repo"
            />
            <p className="text-xs text-muted-foreground">
              HTTPS URL — GitHub, GitLab, or Bitbucket. We'll detect the host and derive a
              suggested name + slug from <span className="font-mono">owner/repo</span>; you can
              override either below. Branch + PAT come in the next step.
            </p>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            required
            {...(role !== 'repo' ? { autoFocus: true } : {})}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="MariaDB"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="slug">Slug</Label>
          <Input
            id="slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="db"
          />
          <p className="text-xs text-muted-foreground">Lowercase, dashes only.</p>
          {slugTaken && (
            <p className="text-xs text-destructive">Already taken in this application.</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="desc">Description</Label>
          <Input
            id="desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
          />
        </div>

        {role === 'repo' && (
          <div className="space-y-1.5">
            <Label htmlFor="pathInRepo">Path in repo</Label>
            <Input
              id="pathInRepo"
              value={pathInRepo}
              onChange={(e) => setPathInRepo(e.target.value)}
              placeholder="."
            />
            <p className="text-xs text-muted-foreground">
              Subdirectory the component lives in. Use{' '}
              <code className="rounded bg-muted px-1 text-[11px]">.</code> for single-repo apps.
            </p>
          </div>
        )}

        {error && (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting || !name || slugTaken || (role === 'repo' && !repoUrl.trim())}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : role === 'repo' ? (
              'Continue'
            ) : (
              'Create component'
            )}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}

/* ---------------------------------------------------------------------------
 * Step 2: GitHub credential + webhook (repo only)
 * ------------------------------------------------------------------------- */

interface GitHubStepProps {
  componentName: string;
  /** Which provider's copy / docs / placeholders to render. */
  scmKind: 'github' | 'gitlab' | 'bitbucket';
  /** Org the new Component (and any new Secret) belongs to. Drives the existing-PAT picker's lookup. */
  applicationOrgId: string;
  pat: string;
  setPat: (s: string) => void;
  /** Id of a picked existing PAT Secret. When set, submit skips createSecretFromPat and uses this directly. */
  existingSecretId: string | null;
  onExistingSecretChange: (id: string | null) => void;
  defaultRef: string;
  setDefaultRef: (s: string) => void;
  branchOptions: string[];
  lookupError: string | null;
  lookingUp: boolean;
  onLookup: () => void;
  registerWebhook: boolean;
  setRegisterWebhook: (b: boolean) => void;
  manualFallback: ManualFallback | null;
  onFinishAfterFallback: () => void;
  submitting: boolean;
  error: string | null;
  onBack: () => void;
  onSkip: () => void;
  onSubmit: (e: FormEvent) => void;
}

function GitHubStep(props: GitHubStepProps): JSX.Element {
  const {
    componentName,
    scmKind,
    applicationOrgId,
    pat,
    setPat,
    existingSecretId,
    onExistingSecretChange,
    defaultRef,
    setDefaultRef,
    branchOptions,
    lookupError,
    lookingUp,
    onLookup,
    registerWebhook,
    setRegisterWebhook,
    manualFallback,
    onFinishAfterFallback,
    submitting,
    error,
    onBack,
    onSkip,
    onSubmit,
  } = props;

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Step 2 of 2
        </div>
        <DialogTitle className="flex items-center gap-2">
          <GitBranch className="h-5 w-5 text-primary" />
          {PROVIDER_LABEL[scmKind]} credential for {componentName}
        </DialogTitle>
        <DialogDescription>
          Paste a Personal Access Token. We'll use it to populate the branch picker, clone the
          repo during discovery, and (if you tick below) register the webhook. Skip to attach
          one later.
        </DialogDescription>
      </DialogHeader>

      <form className="space-y-4" onSubmit={onSubmit}>
        <PatField
          value={pat}
          onChange={setPat}
          scmKind={scmKind}
          existingPat={{
            orgId: applicationOrgId,
            selectedId: existingSecretId,
            onSelect: onExistingSecretChange,
          }}
        />

        {/* Look-up button → /api/v1/scm/lookup → populates the branch
            dropdown. Available with or without a PAT (public repos
            resolve anonymously). After successful lookup the picker
            renders with the default branch pre-selected. */}
        <div className="space-y-1.5">
          <Label htmlFor="defaultRef">Branch</Label>
          <div className="flex gap-2">
            {branchOptions.length > 0 ? (
              <select
                id="defaultRef"
                value={defaultRef}
                onChange={(e) => setDefaultRef(e.target.value)}
                className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {branchOptions.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            ) : (
              <Input
                id="defaultRef"
                value={defaultRef}
                onChange={(e) => setDefaultRef(e.target.value)}
                placeholder="(click Look up to populate)"
                className="flex-1"
                readOnly
              />
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onLookup}
              disabled={lookingUp}
            >
              {lookingUp ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Look up'}
            </Button>
          </div>
          {lookupError && (
            <p className="text-xs text-destructive">{lookupError}</p>
          )}
          {!lookupError && branchOptions.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {branchOptions.length} branch{branchOptions.length === 1 ? '' : 'es'} loaded.
              Default branch pre-selected.
            </p>
          )}
        </div>

        <RegisterWebhookCheckbox
          checked={registerWebhook}
          onChange={setRegisterWebhook}
          /* "hasPat" really means "have we got a credential to use for
             webhook registration" — either a pasted one or a picked
             existing secret. Either gates the upstream call. */
          hasPat={pat.trim().length > 0 || existingSecretId != null}
          scmKind={scmKind}
        />

        {manualFallback && (
          <ManualWebhookFallbackPanel
            fallback={manualFallback}
            onDone={onFinishAfterFallback}
            scmKind={scmKind}
          />
        )}

        {error && (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        {/* `flex-wrap` lets the row break onto a second line when
            button labels + the back/skip group can't fit side-by-side
            at max-w-xl. Without it, long labels would force the footer
            wider than the dialog. Combined with the shortened labels
            below, the common case fits on one line; the worst case
            wraps cleanly instead of busting the modal. */}
        <DialogFooter className="flex-wrap sm:justify-between">
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={onBack} disabled={submitting}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
            <Button type="button" variant="ghost" onClick={onSkip} disabled={submitting}>
              Skip
            </Button>
          </div>
          <Button type="submit" disabled={submitting || Boolean(manualFallback)}>
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : registerWebhook && (pat.trim() || existingSecretId) ? (
              'Create + register webhook'
            ) : (
              'Create component'
            )}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}
